import ReactDOM from 'react-dom/client';
import { Navigate, Route } from 'react-router-dom';
import './index.css';
import { ContactPage } from './pages/Contact';
import { LegalPage } from './pages/Legal/Legal';
import { LoginPage } from './pages/Auth/Login';
import { themeResolver } from './resolvers/themeResolver';
import { RootTemplate } from './templates/RootTemplate';
import { HomePage } from './pages/Home';
import { PropsWithChildren } from 'react';
import { ProfilePage } from './pages/Profile/Profile';
import { useTheme } from './contexts/ThemeProvider';
import { NotificationPage } from './pages/Notifications/Notifications';
import * as Sentry from '@sentry/react';
import { SearchPage } from './pages/Search';
import { DealsPage } from './pages/Deals/Deals';
import { DealsDetailsPage } from './pages/DealsDetails/DealsDetails';
import { FavoritesPage } from './pages/Favorites/Favorites';
import { OrderHistoryRoute } from './pages/OrderHistory/OrderHistory';
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Partner } from './pages/Profile/components/Partner';
import { FormPartner } from './pages/Partner/FormPartner';
import { RecoveryPage } from './pages/Auth/RecoveryPage';
import { TelemedicineSchedulePage } from './pages/Telemedicine/TelemedicineSchedule';
import { FaturaPage } from './pages/Fatura/Fatura';
import { browserTracingIntegration } from '@sentry/react';
import { ProtectedRoute } from './contexts/ProtectedRoute';
import { BibliotecaPage, BancaPage } from './pages/Biblioteca/Biblioteca';
import { BookstoreModuleProvider } from './pages/Bookstore/contexts/BookstoreModuleContext';
import { ProdutosPage } from './pages/Produtos/Produtos';
import { lazy, Suspense } from 'react';

const BookReader = lazy(() =>
  import('./pages/Bookstore/pages/BookReader').then((m) => ({ default: m.BookReader })),
);
const PDFReaderPage = lazy(() =>
  import('./pages/Bookstore/pages/PDFReader').then((m) => ({ default: m.PDFReaderPage })),
);
const AudioBookPage = lazy(() =>
  import('./pages/Bookstore/pages/AudioBook').then((m) => ({ default: m.AudioBookPage })),
);

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels, ArcElement);

ChartJS.defaults.set('plugins.datalabels', {
  backgroundColor: '#dedede',
  borderRadius: 5,
  font: {
    size: 10,
  },
});

function ClubProtectedRoute({ children }: PropsWithChildren) {
  return (
    <ProtectedRoute loginPage="/login" showToast={false}>
      {children}
    </ProtectedRoute>
  );
}

function HomeRedirect() {
  const { productData, disableLibraryModule } = useTheme();

  // com livro e banca no mesmo tenant, abre sempre a biblioteca (onde roda o tutorial)
  const hasBiblioteca = !disableLibraryModule && productData?.hasBookstore;
  const hasBanca = !disableLibraryModule && productData?.hasMagazine;

  if (hasBiblioteca) return <Navigate to="/biblioteca" replace />;
  if (hasBanca) return <Navigate to="/banca" replace />;
  return <HomePage />;
}

if (process.env.REACT_APP_SENTRY_ENVIRONMENT === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '0.05') || 0.05,
    sampleRate: parseFloat(process.env.REACT_APP_SENTRY_SAMPLE_RATE ?? '0.5') || 0.5,
    release: process.env.REACT_APP_SENTRY_RELEASE || 'unknown-release',
    integrations: [browserTracingIntegration()],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network Error',
      'Load failed',
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'cancelled',
      'AbortError',
      'TypeError: Failed to fetch',
      'TypeError: NetworkError',
      'TypeError: cancelled',
      'top.GLOBALS',
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
    ],
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
      /extensions\//i,
      /^chrome:\/\//i,
    ],
    beforeSend(event, hint) {
      if (event.exception) {
        const exception = event.exception.values?.[0];
        if (exception?.type === 'NotFoundError' && exception?.value?.includes('removeChild')) {
          return null;
        }
      }

      const error = hint?.originalException as { response?: { status?: number }; message?: string } | undefined;
      const status = error?.response?.status;
      if (status === 429 || status === 401 || status === 403 || status === 404) {
        return null;
      }

      return event;
    },
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <RootTemplate themeResolver={themeResolver}>
    <Route path={'/login'} element={<LoginPage />} />
    <Route path={'/recuperar'} element={<RecoveryPage />} />
    <Route
      path={'/'}
      element={
        <ClubProtectedRoute>
          <HomeRedirect />
        </ClubProtectedRoute>
      }
    />
    <Route 
      path={'/telemedicina'} 
      element={
        <ClubProtectedRoute>
          <TelemedicineSchedulePage />
        </ClubProtectedRoute>
      } 
    />
    <Route 
      path={'/vantagens'} 
      element={
        <ClubProtectedRoute>
          <DealsPage />
        </ClubProtectedRoute>
      } 
    />
    <Route 
      path={'/vantagens/detalhes/:mode/:id'} 
      element={
        <ClubProtectedRoute>
          <DealsDetailsPage />
        </ClubProtectedRoute>
      } 
    />
    <Route
      path={'/beneficios'}
      element={
        <ClubProtectedRoute>
          <ProdutosPage />
        </ClubProtectedRoute>
      }
    />
    <Route 
      path={'/busca'} 
      element={
        <ClubProtectedRoute>
          <SearchPage />
        </ClubProtectedRoute>
      } 
    />
    <Route 
      path={'/parcerias'} 
      element={
        <>
          <Partner />
        </>
      } 
    />
    <Route
      path={'/produtos'}
      element={
        <ClubProtectedRoute>
          <ProdutosPage />
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/biblioteca'}
      element={
        <ClubProtectedRoute>
          <BibliotecaPage />
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/banca'}
      element={
        <ClubProtectedRoute>
          <BancaPage />
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/leitura/:id'}
      element={
        <ClubProtectedRoute>
          <BookstoreModuleProvider type="biblioteca">
            <Suspense fallback={null}>
              <BookReader />
            </Suspense>
          </BookstoreModuleProvider>
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/pdf/:id'}
      element={
        <ClubProtectedRoute>
          <BookstoreModuleProvider type="biblioteca">
            <Suspense fallback={null}>
              <PDFReaderPage />
            </Suspense>
          </BookstoreModuleProvider>
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/audiobook/:id'}
      element={
        <ClubProtectedRoute>
          <BookstoreModuleProvider type="biblioteca">
            <Suspense fallback={null}>
              <AudioBookPage />
            </Suspense>
          </BookstoreModuleProvider>
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/cadastro-parcerias'} 
      element={
        <>
          <FormPartner />
        </>
      } 
    />
    <Route
      path={'/central-do-cliente'}
      element={
        <ClubProtectedRoute>
          <FaturaPage />
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/notificacoes'}
      element={
        <ClubProtectedRoute>
          <NotificationPage />
        </ClubProtectedRoute>
      }
    />
    {/* <Route
      path={'/compras'}
      element={
        <ClubProtectedRoute>
          <ShoppingPage />
        </ClubProtectedRoute>
      }
    /> */}
    <Route
      path={'/perfil/*'}
      element={
        <ClubProtectedRoute>
          <ProfilePage />
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/favoritos'}
      element={
        <ClubProtectedRoute>
          <FavoritesPage />
        </ClubProtectedRoute>
      }
    />
    <Route
      path={'/historico'}
      element={
        <ClubProtectedRoute>
          <OrderHistoryRoute />
        </ClubProtectedRoute>
      }
    />
    <Route path={'/legal/*'} element={<LegalPage />} />
    <Route path={'/contato'} element={<ContactPage />} />
    <Route path={'*'} element={<Navigate to="/" />} />
  </RootTemplate>
);
